import React, { useState } from "react";
import RequirementPicker from './RequirementPicker';
import FormField, {
  TextInput,
  TextArea,
  SelectInput,
} from "../common/FormField";

const YES = { yes: "Да", no: "Нет", unknown: "Неизвестно" };
const MODES = {
  unknown: "Не уточнено",
  own: "Собственное выполнение",
  external: "Привлечённый исполнитель",
  managed: "Управление внешним процессом",
  customer: "Обеспечивает заказчик",
  not_implemented: "Не обеспечено",
  not_applicable: "Обоснованно неприменимо",
};
export function Tri({ label, value, onChange }) {
  return (
    <FormField label={label}>
      <SelectInput
        value={typeof value === "object" ? value?.value : value || ""}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">Не заполнено</option>
        {Object.entries(YES).map(([k, v]) => (
          <option key={k} value={k}>
            {v}
          </option>
        ))}
      </SelectInput>
    </FormField>
  );
}
export function pathGet(obj, path) {
  return path.split(".").reduce((v, k) => v?.[k], obj);
}
export function pathSet(obj, path, value) {
  const copy = JSON.parse(JSON.stringify(obj)),
    keys = path.split(".");
  let node = copy;
  keys.slice(0, -1).forEach((k) => {
    node[k] = node[k] && typeof node[k] === "object" ? node[k] : {};
    node = node[k];
  });
  node[keys.at(-1)] = value;
  return copy;
}
export function AttributeInput({ attribute, value, onChange }) {
  const a = attribute.data,
    options = a.values || [],
    multiple = /set|array/.test(a.value_type);
  return (
    <FormField label={a.title} hint={a.when_required}>
      {options.length ? (
        <SelectInput
          multiple={multiple}
          value={value ?? (multiple ? [] : "")}
          onChange={(e) =>
            onChange(
              multiple
                ? [...e.target.selectedOptions].map((o) => o.value)
                : e.target.value || undefined,
            )
          }
        >
          {!multiple && <option value="">Не заполнено</option>}
          {options.map((v) => (
            <option key={v} value={v}>
              {YES[v] || v}
            </option>
          ))}
        </SelectInput>
      ) : (
        <TextInput
          type={
            ["number", "integer"].includes(a.value_type) ? "number" : "text"
          }
          value={value ?? ""}
          onChange={(e) =>
            onChange(
              e.target.value === ""
                ? undefined
                : ["number", "integer"].includes(a.value_type)
                  ? Number(e.target.value)
                  : e.target.value,
            )
          }
        />
      )}
    </FormField>
  );
}

export function Assignment({ process, value = {}, onChange }) {
  const patch = (v) => onChange({ ...value, ...v });
  return (
    <details className="classifier-disclosure">
      <summary>
        {process.title}{" "}
        <span className="classifier-muted">
          {MODES[value.implementation || "unknown"]}
        </span>
      </summary>
      <div className="classifier-stack">
        <div className="classifier-form-grid">
          <FormField label="Способ обеспечения">
            <SelectInput
              value={value.implementation || "unknown"}
              onChange={(e) => patch({ implementation: e.target.value })}
            >
              {Object.entries(MODES).map(([k, v]) => (
                <option value={k} key={k}>
                  {v}
                </option>
              ))}
            </SelectInput>
          </FormField>
          <FormField label="Ответственный">
            <TextInput
              value={value.owner || ""}
              onChange={(e) => patch({ owner: e.target.value })}
            />
          </FormField>
          <FormField
            label="Идентификатор общей реализации"
            hint="Одинаковый ID указывают только для действительно общей системы."
          >
            <TextInput
              value={value.process_system_id || ""}
              onChange={(e) => patch({ process_system_id: e.target.value })}
            />
          </FormField>
          <FormField label="Источник назначения">
            <TextInput
              value={value.source || ""}
              onChange={(e) => patch({ source: e.target.value })}
            />
          </FormField>
        </div>
        {value.implementation === "not_applicable" && (
          <div className="classifier-form-grid">
            {[
              ["reason", "Причина исключения"],
              ["basis", "Основание"],
              ["approver", "Кто согласовал"],
              ["date", "Дата согласования"],
            ].map(([k, label]) => (
              <FormField key={k} label={label}>
                <TextInput
                  type={k === "date" ? "date" : "text"}
                  value={value.exception?.[k] || ""}
                  onChange={(e) =>
                    patch({
                      exception: { ...value.exception, [k]: e.target.value },
                    })
                  }
                />
              </FormField>
            ))}
          </div>
        )}
      </div>
    </details>
  );
}

export default function ProfileEditor({
  value,
  onChange,
  refs,
  companies = [],
}) {
  const [active, setActive] = useState(0);
  const choices = refs.choices || {},
    profiles = value.profiles || [],
    p = profiles[active];
  const patch = (v) => onChange({ ...value, ...v });
  const edit = (v) =>
    patch({
      profiles: profiles.map((item, i) =>
        i === active ? { ...item, ...v } : item,
      ),
    });
  const group = choices.group?.find((g) => g.id === p?.group),
    roles = (choices.role || []).filter((r) =>
      group?.data.roles.includes(r.id),
    );
  const attrs = (choices.attribute || []).filter(
    (a) =>
      a.data.scope === "profile" &&
      !String(a.data.value_type || "").startsWith("derived") &&
      (!a.data.group_id || a.data.group_id === p?.group),
  );
  const commonFields = attrs.filter(
    (a) =>
      [
        "text/reference",
        "string",
        "table/reference",
        "proven/new/limited/unknown",
      ].includes(a.data.value_type) && !["site_id"].includes(a.id),
  );
  const techs = (choices.technology || []).filter(
    (t) => t.data.group_id === p?.group,
  );
  const companyFields = (choices.attribute || []).filter(
    (a) =>
      a.data.scope === "company" &&
      a.data.context_path?.startsWith("company.") &&
      !a.data.context_path.startsWith("company.flags.") &&
      !a.data.context_path.includes("<") &&
      !String(a.data.value_type || "").startsWith("derived") &&
      ![
        "company.company_id",
        "company.qualification_basis",
        "company.profiles",
        "company.company_types",
      ].includes(a.data.context_path) &&
      (["number", "integer", "string", "enum", "yes/no/unknown"].includes(
        a.data.value_type,
      ) ||
        (a.data.values || []).length),
  );
  const recordFields = (choices.attribute || []).filter(
    (a) =>
      !(choices.contract_note || []).some((note) => note.id === "technical-record-v1") &&
      a.data.scope === "record" &&
      a.data.context_path?.startsWith("record.") &&
      !a.data.context_path.includes("<") &&
      (!a.data.group_id || a.data.group_id === p?.group),
  );
  const customFields = attrs.filter(
    (a) =>
      a.data.context_path?.startsWith("profile.") &&
      !a.data.context_path.includes("<") &&
      !a.data.context_path.startsWith("profile.functions.") &&
      !a.data.context_path.startsWith("profile.activities.") &&
      ![
        "group",
        "role",
        "category",
        "site_id",
        "control_stages",
        "control_responsibility",
      ].includes(a.data.context_path.slice(8)) &&
      !techs.some((t) => t.data.field === a.data.context_path.slice(8)) &&
      !commonFields.includes(a) &&
      ([
        "yes/no/unknown",
        "number",
        "integer",
        "enum",
        "boolean",
        "string",
      ].includes(a.data.value_type) ||
        (a.data.values || []).length),
  );
  const updateMap = (key, id, val) => edit({ [key]: { ...p[key], [id]: val } });
  const activities = choices.activity || [];
  const relevantProcesses = (choices.process || []).filter(
    (proc) =>
      p?.process_assignments?.[proc.id] ||
      p?.additional_processes?.includes(proc.id) ||
      (choices.route || []).some(
        (r) =>
          r.data.group_id === p?.group &&
          r.data.role_id === p?.role &&
          r.data.processes.includes(proc.id),
      ) ||
      activities.some(
        (a) =>
          a.data.process_id === proc.id &&
          ["yes", "unknown"].includes(
            p?.activities?.[a.id]?.value || p?.activities?.[a.id],
          ),
      ),
  );
  return (
    <div className="classifier-stack">
      <div className="classifier-form-grid">
        <FormField label="Организация">
          <SelectInput
            value={value.company_id || ""}
            onChange={(e) => patch({ company_id: e.target.value })}
          >
            <option value="">Выберите организацию</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </SelectInput>
        </FormField>
        <FormField label="Основание оценки организации">
          <TextInput
            value={value.qualification_basis || ""}
            onChange={(e) => patch({ qualification_basis: e.target.value })}
          />
        </FormField>
      </div>
      <details className="classifier-disclosure">
        <summary>Общие признаки организации</summary>
        <div className="classifier-form-grid">
          {(choices.attribute || [])
            .filter((a) => a.data.context_path?.startsWith("company.flags."))
            .map((a) => (
              <Tri
                key={a.id}
                label={a.data.title}
                value={value.company_flags?.[a.id]}
                onChange={(v) =>
                  patch({
                    company_flags: { ...value.company_flags, [a.id]: v },
                  })
                }
              />
            ))}
        </div>
      </details>
      {!!companyFields.length && (
        <details className="classifier-disclosure" open>
          <summary>Дополнительные атрибуты организации</summary>
          <div className="classifier-form-grid">
            {companyFields.map((a) => (
              <AttributeInput
                key={a.id}
                attribute={a}
                value={pathGet(
                  value.company_attributes || {},
                  a.data.context_path.slice(8),
                )}
                onChange={(v) =>
                  patch({
                    company_attributes: pathSet(
                      value.company_attributes || {},
                      a.data.context_path.slice(8),
                      v,
                    ),
                  })
                }
              />
            ))}
          </div>
        </details>
      )}
      <details className="classifier-disclosure">
        <summary>Корпоративная ответственность за процессы</summary>
        <div className="classifier-stack">
          {Object.keys(value.company_process_assignments || {}).map((id) => (
            <Assignment
              key={id}
              process={
                (choices.process || []).find((p) => p.id === id) || {
                  title: id,
                }
              }
              value={value.company_process_assignments[id]}
              onChange={(v) =>
                patch({
                  company_process_assignments: {
                    ...value.company_process_assignments,
                    [id]: v,
                  },
                })
              }
            />
          ))}
          <FormField label="Добавить корпоративную функцию">
            <SelectInput
              value=""
              onChange={(e) => {
                if (e.target.value)
                  patch({
                    company_process_assignments: {
                      ...value.company_process_assignments,
                      [e.target.value]: { implementation: "unknown" },
                    },
                  });
              }}
            >
              <option value="">Выберите процесс</option>
              {(choices.process || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </SelectInput>
          </FormField>
        </div>
      </details>
      <details className="classifier-disclosure">
        <summary>Условия организации</summary>
        <div className="classifier-form-grid">
          {(choices.condition || []).map((c) => (
            <Tri
              key={c.id}
              label={c.data.question || c.title}
              value={value.company_conditions?.[c.id]}
              onChange={(v) =>
                patch({
                  company_conditions: {
                    ...value.company_conditions,
                    [c.id]: v,
                  },
                })
              }
            />
          ))}
        </div>
        <p className="classifier-muted">
          Условия профиля ниже имеют приоритет. Унаследованное «да» для процесса
          потребует подтверждения.
        </p>
      </details>
      <div className="classifier-toolbar">
        <div
          className="classifier-tabs"
          role="tablist"
          aria-label="Направления деятельности"
        >
          {profiles.map((q, i) => (
            <button
              role="tab"
              aria-selected={i === active}
              className="ap-button ap-button--secondary"
              key={q.id}
              onClick={() => setActive(i)}
              type="button"
            >
              Профиль {i + 1} {q.site_id && "· " + q.site_id}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="ap-button ap-button--primary"
          onClick={() => {
            patch({
              profiles: [
                ...profiles,
                {
                  id: "profile-" + crypto.randomUUID(),
                  activities: {},
                  process_assignments: {},
                },
              ],
            });
            setActive(profiles.length);
          }}
        >
          Добавить профиль
        </button>
      </div>
      {p && (
        <section className="classifier-panel classifier-stack">
          <div className="classifier-form-grid">
            <FormField label="Группа деятельности">
              <SelectInput
                value={p.group || ""}
                onChange={(e) => edit({ group: e.target.value, role: "" })}
              >
                <option value="">Выберите группу</option>
                {(choices.group || []).map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.title} ({g.id})
                  </option>
                ))}
              </SelectInput>
            </FormField>
            <FormField label="Роль в деятельности">
              <SelectInput
                value={p.role || ""}
                onChange={(e) => edit({ role: e.target.value })}
              >
                <option value="">Выберите роль</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.title}
                  </option>
                ))}
              </SelectInput>
            </FormField>
            <FormField label="Площадка / область выполнения">
              <TextInput
                value={p.site_id || ""}
                onChange={(e) => edit({ site_id: e.target.value })}
              />
            </FormField>
          </div>
          {!!customFields.length && (
            <details className="classifier-disclosure" open>
              <summary>Дополнительные атрибуты деятельности</summary>
              <div className="classifier-form-grid">
                {customFields.map((a) => (
                  <AttributeInput
                    key={a.id}
                    attribute={a}
                    value={pathGet(p, a.data.context_path.slice(8))}
                    onChange={(v) =>
                      edit(pathSet(p, a.data.context_path.slice(8), v))
                    }
                  />
                ))}
              </div>
            </details>
          )}
          {(choices.contract_note || []).some((note) => note.id === "technical-record-v1") && <p className="ap-field__hint">Признаки измерения, замены, физического испытания и ремонта заполняются отдельно для каждой технической записи внутри аудита. Здесь они не применяются ко всей компании.</p>}
          {!!recordFields.length && (
            <details className="classifier-disclosure">
              <summary>Контекст выбранной технической записи</summary>
              <p>
                Признаки относятся к записи, а не ко всей организации. Для
                нескольких разных записей используйте отдельные проверочные
                сценарии.
              </p>
              <div className="classifier-form-grid">
                {recordFields.map((a) => (
                  <AttributeInput
                    key={a.id}
                    attribute={a}
                    value={pathGet(
                      p.record || {},
                      a.data.context_path.slice(7),
                    )}
                    onChange={(v) =>
                      edit({
                        record: pathSet(
                          p.record || {},
                          a.data.context_path.slice(7),
                          v,
                        ),
                      })
                    }
                  />
                ))}
              </div>
            </details>
          )}
          {p.site_id && (
            <details className="classifier-disclosure">
              <summary>Условия площадки {p.site_id}</summary>
              <div className="classifier-form-grid">
                {(choices.condition || []).map((c) => (
                  <Tri
                    key={c.id}
                    label={c.title}
                    value={value.site_conditions?.[p.site_id]?.[c.id]}
                    onChange={(v) =>
                      patch({
                        site_conditions: {
                          ...value.site_conditions,
                          [p.site_id]: {
                            ...value.site_conditions?.[p.site_id],
                            [c.id]: v,
                          },
                        },
                      })
                    }
                  />
                ))}
              </div>
            </details>
          )}
          {!!techs.length && (
            <div className="classifier-form-grid">
              {techs.map((t) => (
                <FormField key={t.id} label={t.title}>
                  <SelectInput
                    multiple={t.data.multiple === "yes"}
                    value={
                      p[t.data.field] || (t.data.multiple === "yes" ? [] : "")
                    }
                    onChange={(e) =>
                      edit({
                        [t.data.field]:
                          t.data.multiple === "yes"
                            ? [...e.target.selectedOptions].map((o) => o.value)
                            : e.target.value,
                      })
                    }
                  >
                    <option value="">Не заполнено</option>
                    {t.data.values.map((v) => (
                      <option value={v} key={v}>
                        {t.data.labels[v] || v}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>
              ))}
            </div>
          )}
          <details className="classifier-disclosure">
            <summary>Фактическая деятельность</summary>
            <p className="classifier-muted">
              «Да» означает ответственность за деятельность, а не обязательное
              собственное выполнение. Неизвестность сохраняет вопрос для
              уточнения.
            </p>
            <div className="classifier-form-grid">
              {activities.map((a) => (
                <div key={a.id}>
                  <Tri
                    label={a.title}
                    value={p.activities?.[a.id]}
                    onChange={(v) =>
                      updateMap("activities", a.id, {
                        ...p.activities?.[a.id],
                        value: v,
                      })
                    }
                  />
                  {p.activities?.[a.id] && (
                    <TextInput
                      aria-label={`Основание деятельности ${a.id}`}
                      placeholder="Основание ответа"
                      value={p.activities[a.id]?.basis || ""}
                      onChange={(e) =>
                        updateMap("activities", a.id, {
                          ...p.activities[a.id],
                          basis: e.target.value,
                        })
                      }
                    />
                  )}
                </div>
              ))}
            </div>
          </details>
          <details className="classifier-disclosure">
            <summary>Порученные функции и этапы контроля</summary>
            <div className="classifier-form-grid">
              {attrs
                .filter((a) =>
                  a.data.context_path?.startsWith("profile.functions."),
                )
                .map((a) => (
                  <Tri
                    key={a.id}
                    label={a.title}
                    value={p.functions?.[a.data.context_path.split(".").at(-1)]}
                    onChange={(v) =>
                      updateMap(
                        "functions",
                        a.data.context_path.split(".").at(-1),
                        { value: v },
                      )
                    }
                  />
                ))}
            </div>
            <div className="classifier-checkboxes">
              {(choices.stage || []).map((s) => (
                <label key={s.id}>
                  <input
                    type="checkbox"
                    checked={(p.control_stages || []).includes(s.id)}
                    onChange={(e) =>
                      edit({
                        control_stages: e.target.checked
                          ? [...(p.control_stages || []), s.id]
                          : (p.control_stages || []).filter((k) => k !== s.id),
                      })
                    }
                  />
                  {s.title}
                </label>
              ))}
            </div>
          </details>
          <details className="classifier-disclosure">
            <summary>Условия этого профиля</summary>
            <div className="classifier-form-grid">
              {(choices.condition || []).map((c) => (
                <Tri
                  key={c.id}
                  label={c.title}
                  value={p.conditions?.[c.id]}
                  onChange={(v) => updateMap("conditions", c.id, v)}
                />
              ))}
            </div>
          </details>
          <details className="classifier-disclosure" open>
            <summary>Кто обеспечивает процессы</summary>
            <div className="classifier-stack">
              {relevantProcesses.map((proc) => (
                <Assignment
                  key={proc.id}
                  process={proc}
                  value={p.process_assignments?.[proc.id]}
                  onChange={(v) => updateMap("process_assignments", proc.id, v)}
                />
              ))}
              <FormField label="Добавить обнаруженный процесс">
                <SelectInput
                  value=""
                  onChange={(e) => {
                    if (e.target.value)
                      edit({
                        additional_processes: [
                          ...new Set([
                            ...(p.additional_processes || []),
                            e.target.value,
                          ]),
                        ],
                      });
                  }}
                >
                  <option value="">Выберите процесс</option>
                  {(choices.process || []).map((proc) => (
                    <option key={proc.id} value={proc.id}>
                      {proc.title}
                    </option>
                  ))}
                </SelectInput>
              </FormField>
              {(p.additional_processes || []).map((id) => (
                <FormField key={id} label={`Основание добавления ${id}`}>
                  <TextInput
                    value={p.additional_process_basis?.[id] || ""}
                    onChange={(e) =>
                      updateMap("additional_process_basis", id, e.target.value)
                    }
                  />
                </FormField>
              ))}
            </div>
          </details>
          <details className="classifier-disclosure">
            <summary>Область, возможности и источники</summary>
            <div className="classifier-form-grid">
              {commonFields.map((a) => (
                <FormField
                  key={a.id}
                  label={a.title}
                  hint={a.data.when_required}
                >
                  <TextArea
                    rows={2}
                    value={
                      p[a.data.context_path?.replace("profile.", "")] || ""
                    }
                    onChange={(e) =>
                      edit({
                        [a.data.context_path.replace("profile.", "")]:
                          e.target.value,
                      })
                    }
                  />
                </FormField>
              ))}
            </div>
          </details>
          <details className="classifier-disclosure">
            <summary>Источник сведений и полнота области</summary>
            <div className="classifier-form-grid">
              {[
                ["source", "Источник сведений"],
                ["owner", "Ответственный за сведения"],
                ["date", "Дата сведений"],
                ["scope", "Область сведений"],
              ].map(([key, label]) => (
                <FormField key={key} label={label}>
                  <TextInput
                    type={key === "date" ? "date" : "text"}
                    value={p.provenance?.[key] || ""}
                    onChange={(e) =>
                      updateMap("provenance", key, e.target.value)
                    }
                  />
                </FormField>
              ))}
              <FormField label="Статус сведений">
                <SelectInput
                  value={p.provenance?.status || ""}
                  onChange={(e) =>
                    updateMap("provenance", "status", e.target.value)
                  }
                >
                  <option value="">Не установлен</option>
                  <option value="declared">Заявлено организацией</option>
                  <option value="verified">Проверено аудитором</option>
                  <option value="synthetic">Учебный пример</option>
                </SelectInput>
              </FormField>
              <FormField label="Проверка полноты технической области">
                <TextArea
                  value={p.technical_scope_review || ""}
                  onChange={(e) =>
                    edit({ technical_scope_review: e.target.value })
                  }
                />
              </FormField>
            </div>
          </details>
          <details className="classifier-disclosure">
            <summary>Особая ответственность по отдельной проверке</summary>
            <div className="classifier-stack">
              {Object.entries(p.control_overrides || {}).map(
                ([id, override]) => (
                  <section
                    key={id}
                    className="classifier-panel classifier-stack"
                  >
                    <h3>{choices.requirement?.find(r => r.id === id)?.title || id}</h3>
                    <Assignment
                      process={{ title: "Обеспечение этой проверки" }}
                      value={override}
                      onChange={(v) => updateMap("control_overrides", id, v)}
                    />
                    <div className="classifier-checkboxes">
                      {(choices.stage || []).map((s) => (
                        <label key={s.id}>
                          <input
                            type="checkbox"
                            checked={(override.stages || []).includes(s.id)}
                            onChange={(e) =>
                              updateMap("control_overrides", id, {
                                ...override,
                                stages: e.target.checked
                                  ? [...(override.stages || []), s.id]
                                  : (override.stages || []).filter(
                                      (k) => k !== s.id,
                                    ),
                              })
                            }
                          />
                          {s.title}
                        </label>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="ap-button ap-button--secondary"
                      onClick={() => {
                        const next = { ...p.control_overrides };
                        delete next[id];
                        edit({ control_overrides: next });
                      }}
                    >
                      Убрать уточнение ответственности
                    </button>
                  </section>
                ),
              )}
              <RequirementPicker label="Уточнить ответственность по требованию" requirements={choices.requirement} selected={p.control_overrides} onSelect={id => updateMap('control_overrides', id, {})} />
            </div>
          </details>
          <details className="classifier-disclosure">
            <summary>Обоснованные исключения требований</summary>
            <p>
              Исключение действует только в этом профиле. Нужны причина,
              основание, согласующий и дата; неполное исключение не убирает
              проверку.
            </p>
            <div className="classifier-stack">
              {Object.entries(p.subject_exceptions || {}).map(([id, ex]) => (
                <section className="classifier-panel classifier-stack" key={id}>
                  <strong>{choices.requirement?.find(r => r.id === id)?.title || id}</strong>
                  <div className="classifier-form-grid">
                    {[
                      ["reason", "Причина"],
                      ["basis", "Основание"],
                      ["approver", "Согласующий"],
                      ["date", "Дата"],
                    ].map(([key, label]) => (
                      <FormField key={key} label={label}>
                        <TextInput
                          type={key === "date" ? "date" : "text"}
                          value={ex[key] || ""}
                          onChange={(e) =>
                            updateMap("subject_exceptions", id, {
                              ...ex,
                              [key]: e.target.value,
                            })
                          }
                        />
                      </FormField>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="ap-button ap-button--secondary"
                    onClick={() => {
                      const next = { ...p.subject_exceptions };
                      delete next[id];
                      edit({ subject_exceptions: next });
                    }}
                  >
                    Отменить исключение
                  </button>
                </section>
              ))}
              <RequirementPicker label="Обосновать исключение требования" requirements={choices.requirement} selected={p.subject_exceptions} onSelect={id => updateMap('subject_exceptions', id, {})} />
            </div>
          </details>
          <button
            type="button"
            className="ap-button ap-button--secondary"
            onClick={() => {
              patch({ profiles: profiles.filter((_, i) => i !== active) });
              setActive(0);
            }}
          >
            Убрать этот профиль из предпросмотра
          </button>
        </section>
      )}
    </div>
  );
}
